import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [assignModal, setAssignModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedManager, setSelectedManager] = useState("");
  const [staffList, setStaffList] = useState([]);

  const [form, setForm] = useState({
    name: "",
    location: "",
    status: "Active",
  });

  // ================= RESET =================
  const resetForm = () => {
    setForm({
      name: "",
      location: "",
      status: "Active",
    });
    setEditing(null);
  };

  const closeAssignModal = () => {
    setAssignModal(false);
    setSelectedManager("");
    setSelectedBranch(null);
  };

  // ================= FETCH =================
  const fetchBranches = async () => {
    const res = await fetch("/api/branches", {
      credentials: "include",
    });
    const data = await res.json();

    if (data.status === "success") {
      setBranches(data.data);
    }
  };

  const fetchStaff = async () => {
    const res = await fetch("/api/staff", {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setStaffList(data.data.filter((s) => s.status === "Active"));
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchStaff();
  }, []);

  // ================= CRUD =================
  const openEdit = (branch) => {
    setForm({
      name: branch.name,
      location: branch.location,
      status: branch.status,
    });
    setEditing(branch.id);
    setShowModal(true);
  };

  const saveBranch = async () => {
    if (!form.name || !form.location) {
      alert("Name and location required");
      return;
    }

    try {
      const url = editing
        ? `/api/branches/${editing}`
        : "/api/branches";

      const method = editing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      await fetchBranches();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error(err);
      alert("Error saving branch");
    }
  };

  const removeBranch = async (id) => {
    if (!window.confirm("Delete this branch?")) return;

    try {
      const res = await fetch(`/api/branches/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setBranches((prev) => prev.filter((b) => b.id !== id));
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  // ================= ASSIGN =================
  const assignManager = async () => {
    if (!selectedManager) {
      alert("Select a manager");
      return;
    }

    const res = await fetch(
      `/api/branches/${selectedBranch.id}/manager`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          manager_id: selectedManager,
        }),
      },
    );

    const data = await res.json();

    if (data.status !== "success") {
      alert(data.message);
      return;
    }

    fetchBranches();
    closeAssignModal();
  };

  // ================= UI =================
  return (
    <DashboardLayout>
      <div className="space-y-10">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif">Branches</h1>
          <PermissionButton permissionModule="branches"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-5 py-2 rounded-xl bg-amber-400 text-black font-medium"
          >
            + Add Branch
          </PermissionButton>
        </div>

        {/* TABLE */}
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Branch</th>
                <th className="px-6 py-4 text-left">Location</th>
                <th className="px-6 py-4 text-left">Manager</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {branches.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-white/10 hover:bg-white/5"
                >
                  <td className="px-6 py-4 font-medium">{b.name}</td>
                  <td className="px-6 py-4">{b.location}</td>
                  <td className="px-6 py-4">{b.manager || "-"}</td>

                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        b.status === "Active"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {b.status}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-center"><TableActions>
                    <div className="flex gap-2 justify-center flex-wrap">
                      {/* ASSIGN */}
                      <PermissionButton permissionModule="branches"
                        onClick={() => {
                          setSelectedBranch(b);
                          setSelectedManager(b.manager_id || "");
                          setAssignModal(true);
                        }}
                        className="px-3 py-1 text-xs rounded-lg border border-blue-400 text-blue-400 hover:bg-blue-400/10"
                      >
                        {b.manager ? "Change Manager" : "Assign Manager"}
                      </PermissionButton>

                      {/* EDIT */}
                      <PermissionButton permissionModule="branches" permissionAction="edit"
                        onClick={() => openEdit(b)}
                        className="px-3 py-1 text-xs rounded-lg border border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                      >
                        Edit
                      </PermissionButton>

                      {/* DELETE */}
                      <PermissionButton permissionModule="branches"
                        onClick={() => removeBranch(b.id)}
                        className="px-3 py-1 text-xs rounded-lg border border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        Delete
                      </PermissionButton>
                    </div>
                  </TableActions></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CREATE / EDIT MODAL */}
        {showModal && (
          <Modal
            onClose={() => {
              setShowModal(false);
              resetForm();
            }}
          >
            <h2 className="text-xl font-serif mb-6">
              {editing ? "Edit Branch" : "Add Branch"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                placeholder="Branch Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <input
                placeholder="Location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <PermissionButton permissionModule="branches"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </PermissionButton>

              <PermissionButton permissionModule="branches"
                onClick={saveBranch}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
              >
                {editing ? "Update Branch" : "Save Branch"}
              </PermissionButton>
            </div>
          </Modal>
        )}

        {/* ASSIGN MANAGER MODAL */}
        {assignModal && (
          <Modal onClose={closeAssignModal}>
            <h2 className="text-xl font-serif mb-6">Assign Manager</h2>

            <select
              value={selectedManager || ""}
              onChange={(e) => setSelectedManager(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
            >
              <option value="">Select Manager</option>

              {staffList.filter((s) => s.branch_id === selectedBranch?.id)
                .length === 0 ? (
                <option disabled>No staff in this branch</option>
              ) : (
                staffList
                  .filter((s) => s.branch_id === selectedBranch?.id)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — {s.role}
                    </option>
                  ))
              )}
            </select>

            <div className="flex justify-end gap-4 mt-6">
              <PermissionButton permissionModule="branches"
                onClick={closeAssignModal}
                className="px-6 py-2 border border-white/20 rounded-xl"
              >
                Cancel
              </PermissionButton>

              <PermissionButton permissionModule="branches"
                disabled={!selectedManager}
                onClick={assignManager}
                className={`px-6 py-2 rounded-xl ${
                  selectedManager
                    ? "bg-blue-500 text-white"
                    : "bg-gray-600 text-gray-300"
                }`}
              >
                Assign
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

// ================= MODAL =================
function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <PermissionButton permissionModule="branches"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>
        {children}
      </div>
    </div>
  );
}
